import { action, computed, observable, runInAction } from 'mobx'
import { dayFormat, momentFromDay, nullCleanObject } from '../../../shared-lib/utils'
import { DEFAULT_AVAILABILITY_DAY, MOBILE_AVAILABILITY_ID } from '../../../shared-lib/commonStrings'
import { weekOptions } from '../../../shared-lib/lib/openingHoursHelpers'
import type { $RootStore } from '../../../src/stores/RootStore'

export const AvailabilityStatus = {
  unavailable: 0,
  request: 1,
  instant: 2,
} as const

export interface $ProfessionalAvailability {
  status: number
  day: string
  times: Array<number>
  workingTimes: Array<[number, number]>
  treatmentIds: Array<string>
  postcodeIds: Array<string>
  bookingId?: string
  transportMode?: string
  saas?: boolean
  locationId?: string
  defaultForDay?: number
}

export const defaultAvailabilityData = {
  status: AvailabilityStatus.request,
  day: '2020-01-01',
  times: [],
  workingTimes: [],
  treatmentIds: [],
  postcodeIds: [],
  bookingId: undefined,
  transportMode: undefined,
  locationId: undefined,
}

// A target may have no stored default availability for a location, so build a full seven-day
// skeleton (one record per day of week). Callers can then always find the day they want.
export const normaliseWeek = (
  fetched: $ProfessionalAvailability[] | undefined,
  locationId: string,
): $ProfessionalAvailability[] => {
  return weekOptions.map(({ dayOfWeek }) => fetched?.find(day => day?.defaultForDay === dayOfWeek) ?? {
    ...defaultAvailabilityData,
    defaultForDay: dayOfWeek,
    day: DEFAULT_AVAILABILITY_DAY,
    locationId: locationId || undefined,
  })
}

// Cache key for a default week. The signed-in account's own hours carry no member id.
const weekKey = (locationId: string, memberId?: string) => `${memberId ?? 'self'}:${locationId}`

export default class ProfessionalAvailabilityStore {
  rootStore: $RootStore
  @observable accessor loading = false

  // Every default (regular) week that has been read, by location and whose it is. The three screens
  // that set hours — a member's working hours, a location's opening hours, and the calendar's
  // availability flow — all read this one copy rather than each holding and refetching their own,
  // so a save on any of them is immediately correct on the others.
  @observable accessor defaultWeeks: Record<string, $ProfessionalAvailability[]> = {}

  // One-off days that override the regular week, by the same key. Unlike `defaultWeeks` these are
  // whatever fell inside the range they were read for, so they are a window and not a whole set.
  @observable accessor customDays: Record<string, $ProfessionalAvailability[]> = {}

  @observable accessor editingAvailability: $ProfessionalAvailability = { ...defaultAvailabilityData }
  @observable accessor editingDays: Array<string> = []
  @observable accessor editingLocationIds: Array<string> = []
  @observable accessor editingMemberIds: Array<string> = []

  @action.bound clearData = () => {
    this.loading = false
    this.defaultWeeks = {}
    this.customDays = {}
    this.editingAvailability = { ...defaultAvailabilityData }
    this.editingDays = []
    this.editingLocationIds = []
    this.editingMemberIds = []
  }

  constructor(rootStore: $RootStore) {
    this.rootStore = rootStore
  }

  @action.bound fetchProfessionalAvailability_api = async (
    from: string,
    to: string,
    filter: Record<string, any>,
    memberId?: string,
  ): Promise<$ProfessionalAvailability[] | undefined> => {
    const { communication: { requester } } = this.rootStore
    this.loading = true

    try {
      const response = await requester.getProfessionalAvailability({
        from,
        to: to || from,
        filter,
        memberIds: memberId ? [memberId] : undefined,
      })

      return response.getProfessionalAvailability?.map(a => nullCleanObject(a))
    } catch (e) {
      return undefined
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  fetchDefaultProfessionalAvailability_api = (locationId: string, memberId?: string) => {
    return this.fetchProfessionalAvailability_api(DEFAULT_AVAILABILITY_DAY, DEFAULT_AVAILABILITY_DAY, {
      locationId,
      defaultForDay: { $exists: true },
    }, memberId)
  }

  // The cached week for a location, or undefined until it has been read.
  defaultWeek = (locationId: string, memberId?: string): $ProfessionalAvailability[] | undefined => {
    return this.defaultWeeks[weekKey(locationId, memberId)]
  }

  // Records a week that was just written, so it doesn't have to be read back.
  @action setDefaultWeek = (locationId: string, week: $ProfessionalAvailability[], memberId?: string) => {
    this.defaultWeeks[weekKey(locationId, memberId)] = week
  }

  // For a write that changed weeks without saying which — dropping the lot is cheap, since the
  // next screen to want one reads it again.
  @action invalidateDefaultWeeks = () => {
    this.defaultWeeks = {}
  }

  // Reads several locations' weeks in one request and caches them, so switching location afterwards
  // needs no round trip. Every requested location gets a full seven days, stored or not.
  loadDefaultWeeks_api = async (locationIds: string[], memberId?: string) => {
    if (locationIds.length === 0) return

    const fetched = await this.fetchProfessionalAvailability_api(
      DEFAULT_AVAILABILITY_DAY,
      DEFAULT_AVAILABILITY_DAY,
      { locationId: { $in: locationIds }, defaultForDay: { $exists: true } },
      memberId,
    )

    if (!fetched) return

    runInAction(() => {
      for (const locationId of locationIds) {
        const forLocation = fetched.filter(availability => availability.locationId === locationId)
        this.defaultWeeks[weekKey(locationId, memberId)] = normaliseWeek(forLocation, locationId)
      }
    })
  }

  // The cached one-off days for a location, or undefined until they have been read.
  customDaysFor = (locationId: string, memberId?: string): $ProfessionalAvailability[] | undefined => {
    return this.customDays[weekKey(locationId, memberId)]
  }

  // One-off days in a date range. `from` and `to` must differ: an equal pair means "what applies on
  // this day" to the resolver, which folds in the recurring default for that weekday. Defaults sit
  // at DEFAULT_AVAILABILITY_DAY, so any ordinary `to` leaves them out.
  loadCustomDays_api = async (locationId: string, from: string, to: string, memberId?: string) => {
    const fetched = await this.fetchProfessionalAvailability_api(from, to, {
      locationId,
      bookingId: { $exists: false },
    }, memberId)

    if (!fetched) return

    runInAction(() => {
      this.customDays[weekKey(locationId, memberId)] = fetched
    })
  }

  readCustomDay_api = async (
    day: string,
    locationId: string,
    memberId?: string,
  ): Promise<$ProfessionalAvailability | undefined> => {
    const { communication: { requester } } = this.rootStore

    const response = await requester.getProfessionalAvailability({
      from: day,
      to: momentFromDay(day).add(1, 'day').format(dayFormat),
      filter: { locationId, bookingId: { $exists: false } },
      memberIds: memberId ? [memberId] : undefined,
    })

    const availability: Array<$ProfessionalAvailability> =
      response.getProfessionalAvailability?.map(a => nullCleanObject(a)) || []

    return availability.find(a => a.day === day)
  }

  // One location, for the paths that only ever need the one.
  loadDefaultWeek_api = async (locationId: string, memberId?: string) => {
    const fetched = await this.fetchDefaultProfessionalAvailability_api(locationId, memberId)

    if (!fetched) return

    runInAction(() => {
      this.defaultWeeks[weekKey(locationId, memberId)] = normaliseWeek(fetched, locationId)
    })
  }

  @action.bound markAsUnavailable = async (days: string[], locationIds: string[]) => {
    const promises = []
    this.editingAvailability.status = 1
    this.editingAvailability.times = []
    this.editingAvailability.workingTimes = []
    this.editingAvailability.defaultForDay = undefined

    for (const day of days) {
      this.editingAvailability.day = day
      for (const locationId of locationIds) {
        promises.push(this.createOrUpdateProfessionalAvailability_api(
          { ...this.editingAvailability, locationId },
          [day],
        ))
      }
    }
    return await Promise.all(promises)
  }

  restoreDefaultAvailability = async (days: string[], locationIds: string[], memberIds: string[] = []) => {
    const promises = []
    // No members selected → a single undefined target removes the salon's own overrides;
    // otherwise remove each selected member's override (scoped by memberId).
    const memberTargets = memberIds.length > 0 ? memberIds : [undefined]
    for (const day of days) {
      for (const locationId of locationIds) {
        for (const memberId of memberTargets) {
          promises.push(this.removeProfessionalAvailability_api({ day, locationId }, memberId))
        }
      }
    }
    return await Promise.all(promises)
  }

  @action.bound createOrUpdateProfessionalAvailability_api = async (
    availabilityData: Partial<$ProfessionalAvailability> & Pick<$ProfessionalAvailability, 'day'>,
    days?: Array<string>,
    memberId?: string,
  ): Promise<any> => {
    const { communication: { requester } } = this.rootStore

    this.loading = true

    const applyToDays = days && days.length > 0 ? days : [availabilityData.day]

    const data = { availabilityData, applyToDays, memberId }

    try {
      const { createOrUpdateProfessionalAvailability: availability } = await requester.createOrUpdateProfessionalAvailability({ data })

      // Add updated records to the cached weeks, so the screens reading them don't have to re-fetch.
      runInAction(() => {
        for (const saved of availability ?? []) {
          const record: $ProfessionalAvailability | null = nullCleanObject(saved)

          if (!record?.locationId || record.day !== DEFAULT_AVAILABILITY_DAY || record.defaultForDay == null) continue

          const key = weekKey(record.locationId, memberId)
          const week = this.defaultWeeks[key] ?? normaliseWeek(undefined, record.locationId)

          // Patch the one weekday that was written — assigning a single-element array here would
          // drop the other six days from the cache.
          this.defaultWeeks[key] = week.map(day => day.defaultForDay === record.defaultForDay
            ? { ...defaultAvailabilityData, ...record }
            : day)
        }
      })

    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound createDefaultAvailability_api = async (locationId: string) => {
    const { communication: { requester } } = this.rootStore

    this.loading = true

    try {
      await requester.createDefaultAvailability({ data: { locationId } })
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound removeProfessionalAvailability_api = async (data: ProfessionalAvailabilityInput, memberId?: string): Promise<any> => {
    const { communication: { requester } } = this.rootStore

    this.loading = true

    try {
      await requester.removeProfessionalAvailability({ filter: { ...data }, memberId })
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  removeLocationAvailability_api = async (locationId: string, memberId?: string) => {
    await this.removeProfessionalAvailability_api({ locationId }, memberId)

    runInAction(() => {
      const key = weekKey(locationId, memberId)

      this.defaultWeeks[key] = normaliseWeek(undefined, locationId)
      this.customDays[key] = []
    })
  }

  @action.bound setEditingAvailability = (availability: $ProfessionalAvailability | undefined, day = '') => {
    const { professionalProfile } = this.rootStore.userStore.data
    const transportMode = availability?.transportMode || professionalProfile.defaultTransportMode || 'driving'

    this.editingAvailability = {
      ...defaultAvailabilityData,
      ...availability,
      day,
    }

    // If new record set defaults
    if (this.editingAvailability.postcodeIds.length === 0) {
      this.editingAvailability.postcodeIds = [...professionalProfile.postcodeIds]
    }

    if (this.editingAvailability.treatmentIds.length === 0) {
      this.editingAvailability.treatmentIds = [...professionalProfile.treatmentIds]
    }

    if (!this.editingAvailability.transportMode) {
      this.editingAvailability.transportMode = transportMode
    }
  }

  @computed get editingLocations() {
    const { staticLocations, mobileLocation } = this.rootStore.userStore.data.professionalProfile.saasSettings

    return [
      ...(staticLocations || []).filter(location => this.editingLocationIds.includes(location._id)),
      ...(this.editingLocationIds.some(id => [MOBILE_AVAILABILITY_ID, mobileLocation?._id].includes(id)) ? [mobileLocation] : []),
    ]
  }
}
